import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";

const TimerContext = createContext(null);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
};

// Default rest times by level (in seconds)
const REST_TIMES_BY_LEVEL = {
  principiante: 60,
  intermedio: 90,
  avanzado: 90,
  goat: 120,
};

export const TimerProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(90);
  const [exerciseName, setExerciseName] = useState("");
  const [nextExerciseName, setNextExerciseName] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    // Create audio context for the beep sound
    audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Timer logic
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            // Timer finished
            playSound();
            vibrate();
            setIsActive(false);
            return 0;
          }
          // Play tick sound at 3, 2, 1
          if (prev <= 4) {
            playTick();
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeLeft]);

  const playSound = useCallback(() => {
    try {
      if (audioRef.current) {
        const oscillator = audioRef.current.createOscillator();
        const gainNode = audioRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioRef.current.destination);
        
        oscillator.frequency.value = 880; // A5 note
        oscillator.type = "sine";
        gainNode.gain.value = 0.3;
        
        oscillator.start();
        
        // Play 3 beeps
        setTimeout(() => oscillator.stop(), 150);
        setTimeout(() => {
          const osc2 = audioRef.current.createOscillator();
          const gain2 = audioRef.current.createGain();
          osc2.connect(gain2);
          gain2.connect(audioRef.current.destination);
          osc2.frequency.value = 880;
          osc2.type = "sine";
          gain2.gain.value = 0.3;
          osc2.start();
          setTimeout(() => osc2.stop(), 150);
        }, 200);
        setTimeout(() => {
          const osc3 = audioRef.current.createOscillator();
          const gain3 = audioRef.current.createGain();
          osc3.connect(gain3);
          gain3.connect(audioRef.current.destination);
          osc3.frequency.value = 1046.5; // C6 note (higher)
          osc3.type = "sine";
          gain3.gain.value = 0.3;
          osc3.start();
          setTimeout(() => osc3.stop(), 300);
        }, 400);
      }
    } catch (e) {
      // Audio context may not be available - silently ignore
    }
  }, []);

  const playTick = useCallback(() => {
    try {
      if (audioRef.current) {
        const oscillator = audioRef.current.createOscillator();
        const gainNode = audioRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioRef.current.destination);
        
        oscillator.frequency.value = 440;
        oscillator.type = "sine";
        gainNode.gain.value = 0.1;
        
        oscillator.start();
        setTimeout(() => oscillator.stop(), 50);
      }
    } catch (e) {
      // Audio context may not be available - silently ignore
    }
  }, []);

  const vibrate = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 400]);
    }
  }, []);

  const startTimer = useCallback((seconds, exercise = "", nextExercise = "") => {
    // Resume audio context if suspended (iOS requirement)
    if (audioRef.current?.state === "suspended") {
      audioRef.current.resume();
    }
    
    setTotalTime(seconds);
    setTimeLeft(seconds);
    setExerciseName(exercise);
    setNextExerciseName(nextExercise);
    setIsActive(true);
    setIsMinimized(false);
  }, []);

  const pauseTimer = useCallback(() => {
    setIsActive(false);
  }, []);

  const resumeTimer = useCallback(() => {
    if (timeLeft > 0) {
      setIsActive(true);
    }
  }, [timeLeft]);

  const stopTimer = useCallback(() => {
    setIsActive(false);
    setTimeLeft(0);
    setIsMinimized(false);
  }, []);

  const adjustTime = useCallback((delta) => {
    setTimeLeft((prev) => Math.max(0, prev + delta));
    setTotalTime((prev) => Math.max(0, prev + delta));
  }, []);

  const minimizeTimer = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeTimer = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const getDefaultRestTime = useCallback((level) => {
    return REST_TIMES_BY_LEVEL[level?.toLowerCase()] || 90;
  }, []);

  const value = {
    isActive,
    timeLeft,
    totalTime,
    exerciseName,
    nextExerciseName,
    isMinimized,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    adjustTime,
    minimizeTimer,
    maximizeTimer,
    getDefaultRestTime,
    REST_TIMES_BY_LEVEL,
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};
